/**
 * Field Configuration Types
 * Shared types for field configuration system.
 * This file is separate to avoid circular dependencies with generated configs.
 */

// Field type definitions
export enum FieldType {
  TEXT = 'text',
  TEXTAREA = 'textarea',
  NUMBER = 'number',
  SELECT = 'select',
  CHECKBOX = 'checkbox',
  MARKDOWN = 'markdown',
  JSON = 'json',
  AGENT_MULTI_SELECT = 'agent_multi_select', // Multi-select from available agents
  MODEL_SELECTOR = 'model_selector', // Rich model selector with pricing/provider info
  OUTPUT_FORMAT = 'output_format', // Output format selector with schema management
}

// Validation rule types
export enum ValidationRuleType {
  REQUIRED = 'required',
  MIN = 'min',
  MAX = 'max',
  MIN_LENGTH = 'minLength',
  MAX_LENGTH = 'maxLength',
  PATTERN = 'pattern',
  CUSTOM = 'custom',
  IN_OPTIONS = 'inOptions',
}

export interface ValidationRule {
  rule: ValidationRuleType;
  value?: any;
  message?: string;
  pattern?: RegExp;
  options?: string[];
  getOptions?: (appContext: any) => string[];
  validate?: (value: any, appContext?: any) => ValidationResult;
}

export interface ValidationResult {
  valid: boolean;
  isWarning?: boolean;
  message?: string;
}

export interface SelectOption {
  value: string;
  label: string;
}

export interface FieldSchema {
  type: FieldType;
  label: string | (() => string);
  section: string;
  required?: boolean;
  default?: any;
  placeholder?: string | (() => string);
  help?: string | (() => string) | Record<string, string>;
  readonly?: boolean;

  // Select field options
  options?: SelectOption[];
  optionsSource?: string; // Dynamic options from app context
  getOptionsSource?: (config: any) => string; // Dynamic options source based on form state
  optionsFromConfig?: string; // Dynamic options from current config (e.g., 'output_schemas')
  optionsMapper?: (item: any) => SelectOption; // Map config items to SelectOption
  allowEmpty?: boolean; // Always show empty option (for nullable fields)
  emptyLabel?: string; // Label for empty option (default: '-- Select --')

  // Number field constraints
  min?: number;
  max?: number;
  step?: number;

  // Textarea/Markdown rows
  rows?: number;

  // Conditional display
  showIf?: (config: any) => boolean;
  /** Simple path-based showIf for generated configs (converted to showIf function at runtime) */
  showIfPath?: string;
  /** Disable field based on config state (field still visible but not editable) */
  disabledIf?: (config: any) => boolean;

  // Validation rules
  validation?: ValidationRule[];
  invalidMessage?: (value: any) => string;

  // JSON field specific
  displayMode?: 'list' | 'textarea';
  hasToolBrowser?: boolean;

  // Special buttons
  hasResetButton?: boolean;

  // Model selector specific
  /** Parameter field names to show inside this model selector's expandable section */
  linkedParameters?: string[];
  /** Render as primary model with enhanced styling */
  isPrimary?: boolean;
  /** Show feature enable/disable toggle in card header (for optional features) */
  hasFeatureToggle?: boolean;
  /** Hook ID for bidirectional sync with hooks pipeline (e.g., 'vision', 'stt') */
  featureHookId?: string;
  /** Default model to select when feature is enabled (if different from field default) */
  defaultEnabledModel?: string;

  // Select field rendering
  /** Render select as button group instead of dropdown */
  buttonGroup?: boolean;

  // Layout
  /** Make this field span full width in 2-column layouts */
  fullWidth?: boolean;

  // i18n
  /** Skip i18n translation merging - use schema values directly */
  overrideI18n?: boolean;
}

export type FieldConfig = Record<string, FieldSchema>;
